/**
 * mp4-tags.js — window.__mp4Tags
 * ─────────────────────────────────────────────────────────────────────────────
 * Перенести ОПИСАТЕЛЬНЫЕ теги (название, исполнитель, альбом, год, жанр…) из
 * исходного mp4 в выход, который собрал наш muxer.
 *
 * ЗАЧЕМ (обращение #8219, 20.09.2026, /video-watermark, Левон Айрапетов):
 *   «вместо название песни и исполнителя после скачивание, только название в
 *    видео watermarked.mp4 остальное всё исчезает… а до обработки это всё было»
 *   — и следом важнейшая половина: «Другие инструменты в видео, обрезка,
 *   улучшение видео, на название видео не влияют, вот только тут такое».
 *
 * Он прав, и причина — не в туле, а в ЛЕЙНЕ. У одного и того же тула две
 * дороги к одному и тому же файлу, и они отвечают ПО-РАЗНОМУ:
 *
 *   ЗАМЕР 22.09.2026, настоящий Chrome, одна кнопка, один вход
 *   («Billie Eilish - Bad Guy.mp4», title/artist/album/date/genre):
 *
 *     лейн                         теги на выходе
 *     WebCodecs + mp4-muxer        НЕТ НИ ОДНОГО      ← умолчание всего Chromium
 *     FFmpeg-WASM (запасной)       все пять на месте  ← Firefox и старые сборки
 *
 * FFmpeg переносит теги сам (`-map_metadata 0` — его умолчание), а mp4-muxer
 * не умеет их в принципе: во всей библиотеке нет ни одного упоминания `udta`
 * или `ilst`. То есть быстрый лейн ТИХО теряет то, что медленный сохраняет, и
 * ни один прибор этого не видит — файл валиден, играет, размер правдоподобен.
 * Комментарий в самом туле при этом утверждал обратное: «WebCodecs (modern,
 * proper MP4 with metadata)» — премиса, которую никто не проверил.
 *
 * ПОЧЕМУ КОПИЯ, А НЕ ТАБЛИЦА ПОЛЕЙ
 * ────────────────────────────────
 * Теги не разбираются на «title/artist/…» и не собираются заново: дочерние
 * коробки `ilst` переносятся ПОБАЙТОВО. Из этого следуют три вещи, каждая из
 * которых иначе стоила бы отдельной ошибки:
 *   1. нет второй таблицы соответствий (первая живёт в mp4-meta-scrub.js) —
 *      значит нечему разъехаться молча;
 *   2. нет перекодировки строк — кириллица, CJK и эмодзи переезжают теми же
 *      байтами, что лежали в источнике (класс #3792 «РџСѓСЃС‚РѕС‚Р°»);
 *   3. приватные атомы НЕ ПЕРЕНОСЯТСЯ ПО ПОСТРОЕНИЮ: GPS телефона живёт в
 *      `moov/udta/©xyz`, а модель и прошивка — в `moov/meta` (Apple keys/mdta);
 *      обе ветки лежат ВНЕ `udta/meta/ilst`, который мы копируем. Это
 *      осознанно строже ffmpeg: он переносит и их.
 *
 * ЦЕНА
 * ────
 * Файл не читается целиком НИ РАЗУ — ни вход, ни выход. У входа читается
 * только `moov` (КБ), у выхода `moov` переписывается, а `mdat` остаётся
 * ссылками `blob.slice()`. Тот же приём и та же арифметика смещений, что у
 * VRMetadataInjector (см. mp4-vr-metadata.js): врезка в `moov` двигает `mdat`,
 * поэтому таблицы `stco`/`co64` обязаны быть пропатчены.
 *
 * API
 * ───
 *   __mp4Tags.carry(srcFile, outBlob, {slug}) → Promise<Blob>   ← передняя дверь
 *   __mp4Tags.read(fileOrBlob)                → Promise<{count, types:[…]}>
 *   __mp4Tags.write(outBlob, items)           → Promise<Blob>
 *   __mp4Tags.pageSource()                    → File|null  (источник со страницы)
 *
 * Fail-open ВЕЗДЕ: любая неожиданность (не mp4, фрагментированный, нет moov,
 * битая коробка) — возвращается ИСХОДНЫЙ выход без изменений. Потерять теги
 * плохо; испортить человеку готовый файл — несопоставимо хуже.
 *
 * UMD: browser (window.__mp4Tags) + Node (module.exports), чтобы проверять без
 * браузера — тот же контракт, что у output-filename.js.
 */
(function (root, factory) {
    if (typeof module === 'object' && module.exports) module.exports = factory();
    else root.__mp4Tags = factory();
})(typeof self !== 'undefined' ? self : this, function () {
    'use strict';

    /* Атомы `ilst`, которые НЕ переносим.
       `©too` / `©swr` — «чем закодировано»: кодировали теперь мы, и старое имя
       кодировщика в нашем выходе — ложь (ffmpeg тоже перезаписывает его своим). */
    var DENY = { '\xa9too': 1, '\xa9swr': 1 };

    /* Потолок на ВЕСЬ перенесённый блок. Обложка в 4K раздувает moov, а moov
       у faststart-файла читается плеером до первого кадра. 4 МиБ — с запасом
       на обложку и заведомо меньше любого разумного moov. */
    var MAX_BYTES = 4 * 1024 * 1024;

    /* Прямые дети `udta` в стиле QuickTime (старые .mov, некоторые монтажки):
       значение лежит без коробки `data`. Переносим только описательные.
       ⚠️ `©xyz` (координаты съёмки) в этом списке отсутствует НАМЕРЕННО. */
    var QT_KEEP = {
        '\xa9nam': 1, '\xa9ART': 1, '\xa9alb': 1, '\xa9day': 1,
        '\xa9cmt': 1, '\xa9gen': 1, '\xa9wrt': 1, '\xa9cpy': 1,
        '\xa9des': 1, '\xa9dir': 1, '\xa9lyr': 1
    };

    /* ── байтовые примитивы ─────────────────────────────────────────────── */

    function rd32(b, o) { return ((b[o] << 24) | (b[o + 1] << 16) | (b[o + 2] << 8) | b[o + 3]) >>> 0; }
    function wr32(b, o, v) { b[o] = (v >>> 24) & 255; b[o + 1] = (v >>> 16) & 255; b[o + 2] = (v >>> 8) & 255; b[o + 3] = v & 255; }
    function rd64(b, o) { return rd32(b, o) * 0x100000000 + rd32(b, o + 4); }
    function typ(b, o) { return String.fromCharCode(b[o], b[o + 1], b[o + 2], b[o + 3]); }
    function wrTyp(b, o, t) { for (var i = 0; i < 4; i++) b[o + i] = t.charCodeAt(i) & 255; }
    function cat(parts) {
        var n = 0, i;
        for (i = 0; i < parts.length; i++) n += parts[i].length;
        var out = new Uint8Array(n), w = 0;
        for (i = 0; i < parts.length; i++) { out.set(parts[i], w); w += parts[i].length; }
        return out;
    }

    /* Прямые дети области [start,end). Заголовок запоминается, чтобы
       вызывающий не пересчитывал его для 64-битных коробок. */
    function children(b, start, end) {
        var out = [], p = start;
        while (p + 8 <= end) {
            var size = rd32(b, p), t = typ(b, p + 4), hdr = 8;
            if (size === 1) { if (p + 16 > end) break; size = rd64(b, p + 8); hdr = 16; }
            else if (size === 0) size = end - p;
            if (size < hdr || p + size > end) break;
            out.push({ type: t, start: p, size: size, hdr: hdr, dataStart: p + hdr, dataEnd: p + size });
            p += size;
        }
        return out;
    }
    function pick(list, t) { for (var i = 0; i < list.length; i++) if (list[i].type === t) return list[i]; return null; }

    /* ── чтение: найти `moov/udta/meta[mdir]/ilst` ──────────────────────── */

    /* `meta` внутри `udta` — FullBox (4 байта версии и флагов перед детьми) в
       mp4 и ОБЫЧНАЯ коробка в старом QuickTime. Отличаем по содержимому: если
       разбор со сдвигом даёт `hdlr`, значит FullBox на месте. */
    function metaKids(b, meta) {
        var asFull = children(b, meta.dataStart + 4, meta.dataEnd);
        if (asFull.length && pick(asFull, 'hdlr')) return asFull;
        var asPlain = children(b, meta.dataStart, meta.dataEnd);
        if (asPlain.length && pick(asPlain, 'hdlr')) return asPlain;
        return asFull.length ? asFull : asPlain;
    }

    function isMdir(b, kids) {
        var h = pick(kids, 'hdlr');
        /* hdlr: FullBox(4) + pre_defined(4) + handler_type(4) */
        return !!h && h.dataEnd - h.dataStart >= 12 && typ(b, h.dataStart + 8) === 'mdir';
    }

    function moovIn(b) { return pick(children(b, 0, b.length), 'moov'); }
    function udtaIn(b, moov) { return moov ? pick(children(b, moov.dataStart, moov.dataEnd), 'udta') : null; }

    /** moov-байты → массив НЕИЗМЕНЁННЫХ коробок ilst, пригодных к переносу. */
    function collectItems(b) {
        var moov = moovIn(b);
        var udta = udtaIn(b, moov);
        if (!udta) return [];
        var uKids = children(b, udta.dataStart, udta.dataEnd);
        var items = [], total = 0, i, it;

        var meta = pick(uKids, 'meta');
        if (meta) {
            var mKids = metaKids(b, meta);
            var ilst = pick(mKids, 'ilst');
            if (ilst && isMdir(b, mKids)) {
                var kids = children(b, ilst.dataStart, ilst.dataEnd);
                for (i = 0; i < kids.length; i++) {
                    it = kids[i];
                    if (DENY[it.type]) continue;
                    if (total + it.size > MAX_BYTES) break;
                    total += it.size;
                    items.push({ type: it.type, bytes: b.subarray(it.start, it.start + it.size) });
                }
            }
        }

        /* Запасной путь: QuickTime-атомы прямо под `udta` (без `meta/ilst`).
           Заворачиваем значение в коробку `data`, как это делает ffmpeg. */
        if (!items.length) {
            for (i = 0; i < uKids.length; i++) {
                it = uKids[i];
                if (!QT_KEEP[it.type]) continue;
                var v = qtText(b, it);
                if (!v) continue;
                var boxed = buildItem(it.type, v);
                if (total + boxed.length > MAX_BYTES) break;
                total += boxed.length;
                items.push({ type: it.type, bytes: boxed });
            }
        }
        return items;
    }

    /* QuickTime-атом: 2 байта длины + 2 байта языка + текст. Шапки может не
       быть вовсе — тогда читаем тело целиком. */
    function qtText(b, box) {
        var at = box.dataStart, end = box.dataEnd, body = null;
        if (end - at >= 4) {
            var len = (b[at] << 8) | b[at + 1];
            if (len > 0 && at + 4 + len <= end) body = b.subarray(at + 4, at + 4 + len);
        }
        if (!body) body = b.subarray(at, end);
        try { return new TextDecoder('utf-8').decode(body).replace(/\u0000+$/, '').trim(); }
        catch (_) { return ''; }
    }

    /* ── запись: собрать udta/meta/hdlr+ilst ────────────────────────────── */

    /* Одна запись ilst: <fourcc>{ data(version=0, type=1 UTF-8, locale=0) }.
       Байт в байт та же раскладка, что пишет ffmpeg (снята с его же выхода). */
    function buildItem(fourcc, text) {
        var payload;
        try { payload = new TextEncoder().encode(text); }
        catch (_) { payload = Uint8Array.from(unescape(encodeURIComponent(text)), function (c) { return c.charCodeAt(0); }); }
        var data = new Uint8Array(16 + payload.length);
        wr32(data, 0, data.length); wrTyp(data, 4, 'data');
        wr32(data, 8, 1);            /* version(0) + flags(0,0,1) = тип «UTF-8» */
        wr32(data, 12, 0);           /* locale */
        data.set(payload, 16);
        var item = new Uint8Array(8 + data.length);
        wr32(item, 0, item.length); wrTyp(item, 4, fourcc);
        item.set(data, 8);
        return item;
    }

    function buildUdta(items) {
        if (!items || !items.length) return null;
        var body = cat(items.map(function (i) { return i.bytes; }));

        var ilst = new Uint8Array(8 + body.length);
        wr32(ilst, 0, ilst.length); wrTyp(ilst, 4, 'ilst'); ilst.set(body, 8);

        /* hdlr: size + 'hdlr' + ver/flags + pre_defined + 'mdir' + 'appl'
           + 2×reserved + пустое имя (один нулевой байт) = 33 байта. */
        var hdlr = new Uint8Array(33);
        wr32(hdlr, 0, 33); wrTyp(hdlr, 4, 'hdlr');
        wrTyp(hdlr, 16, 'mdir'); wrTyp(hdlr, 20, 'appl');

        var meta = new Uint8Array(12 + hdlr.length + ilst.length);
        wr32(meta, 0, meta.length); wrTyp(meta, 4, 'meta');   /* FullBox: 4 нулевых байта */
        meta.set(hdlr, 12); meta.set(ilst, 12 + hdlr.length);

        var udta = new Uint8Array(8 + meta.length);
        wr32(udta, 0, udta.length); wrTyp(udta, 4, 'udta'); udta.set(meta, 8);
        return udta;
    }

    /* ── врезка в moov ──────────────────────────────────────────────────── */

    /* Смещения чанков (`stco`/`co64`) — АБСОЛЮТНЫЕ позиции в файле. Врезка в
       конец moov двигает всё, что лежит ЗА ним: у faststart-файла это весь
       mdat, у файла с moov в хвосте — ничего. Одно правило покрывает оба
       случая: сдвигается смещение, которое ≥ позиции врезки. */
    function offsetTables(b, moov) {
        var tables = [], traks = children(b, moov.dataStart, moov.dataEnd);
        for (var i = 0; i < traks.length; i++) {
            if (traks[i].type !== 'trak') continue;
            var mdia = pick(children(b, traks[i].dataStart, traks[i].dataEnd), 'mdia'); if (!mdia) continue;
            var minf = pick(children(b, mdia.dataStart, mdia.dataEnd), 'minf'); if (!minf) continue;
            var stbl = pick(children(b, minf.dataStart, minf.dataEnd), 'stbl'); if (!stbl) continue;
            var kids = children(b, stbl.dataStart, stbl.dataEnd);
            for (var k = 0; k < kids.length; k++) {
                var t = kids[k];
                if (t.type !== 'stco' && t.type !== 'co64') continue;
                tables.push({ entries: t.dataStart + 8, count: rd32(b, t.dataStart + 4), elem: t.type === 'co64' ? 8 : 4 });
            }
        }
        return tables;
    }

    /** moov-байты + абсолютная позиция moov + udta → НОВЫЕ moov-байты. */
    function injectMoov(moovBytes, moovFileStart, udta) {
        var moov = moovIn(moovBytes);
        if (!moov || moov.start !== 0) throw new Error('mp4-tags: no moov');
        if (udtaIn(moovBytes, moov)) throw new Error('mp4-tags: udta present');

        var insertAt = moov.dataEnd;                     /* последним ребёнком moov */
        var insertPos = moovFileStart + insertAt;        /* абсолютная позиция в файле */
        var grow = udta.length;
        var tables = offsetTables(moovBytes, moov);

        var out = new Uint8Array(moovBytes.length + grow);
        out.set(moovBytes.subarray(0, insertAt), 0);
        out.set(udta, insertAt);
        out.set(moovBytes.subarray(insertAt), insertAt + grow);

        /* Все таблицы лежат ДО точки врезки (она в самом конце moov), поэтому
           позиция записи в новом буфере совпадает со старой. */
        for (var i = 0; i < tables.length; i++) {
            var tb = tables[i];
            for (var e = 0; e < tb.count; e++) {
                var at = tb.entries + e * tb.elem;
                if (tb.elem === 4) {
                    var v = rd32(out, at);
                    if (v >= insertPos) wr32(out, at, v + grow);
                } else {
                    var v64 = rd64(out, at);
                    if (v64 >= insertPos) {
                        var nv = v64 + grow;
                        wr32(out, at, Math.floor(nv / 0x100000000));
                        wr32(out, at + 4, nv >>> 0);
                    }
                }
            }
        }

        if (moov.hdr === 16) {
            var ns = rd64(out, 8) + grow;
            wr32(out, 8, Math.floor(ns / 0x100000000)); wr32(out, 12, ns >>> 0);
        } else {
            wr32(out, 0, moov.size + grow);
        }
        return out;
    }

    /* ── работа с Blob: только заголовки и moov, mdat остаётся ссылкой ──── */

    var MOOV_MAX = 96 * 1024 * 1024;

    async function topBoxes(blob) {
        var out = [], p = 0, guard = 0;
        while (p + 8 <= blob.size && guard++ < 4096) {
            var h = new Uint8Array(await blob.slice(p, Math.min(blob.size, p + 16)).arrayBuffer());
            if (h.length < 8) break;
            var size = rd32(h, 0), t = typ(h, 4), hdr = 8;
            if (size === 1) { if (h.length < 16) break; size = rd64(h, 8); hdr = 16; }
            else if (size === 0) size = blob.size - p;
            if (size < hdr || p + size > blob.size) break;
            out.push({ type: t, start: p, size: size });
            p += size;
        }
        return out;
    }

    async function moovOf(blob) {
        var boxes = await topBoxes(blob);
        if (!boxes.length || !pick(boxes, 'ftyp')) return null;   /* не mp4-семейство */
        if (pick(boxes, 'moof')) return null;                     /* фрагментированный */
        var moov = pick(boxes, 'moov');
        if (!moov || moov.size > MOOV_MAX) return null;
        return { box: moov, bytes: new Uint8Array(await blob.slice(moov.start, moov.start + moov.size).arrayBuffer()) };
    }

    /* Похоже ли на mp4-семейство ПО ИМЕНИ И ТИПУ. Дешёвая отсечка перед
       чтением байтов; окончательный ответ всё равно даёт `moovOf` (нет ftyp и
       moov — не наш файл, что бы ни было написано в имени). */
    function looksMp4(f) {
        var n = String((f && f.name) || '').toLowerCase();
        var t = String((f && f.type) || '').toLowerCase();
        if (/\.(mp4|m4v|mov|m4a|3gp|3g2)$/.test(n)) return true;
        return t === 'video/mp4' || t === 'video/quicktime' || t === 'audio/mp4';
    }

    /** Что несёт файл: сколько описательных атомов и какие. */
    async function read(blob) {
        try {
            var m = await moovOf(blob);
            if (!m) return { count: 0, types: [] };
            var items = collectItems(m.bytes);
            return { count: items.length, types: items.map(function (i) { return i.type; }) };
        } catch (_) { return { count: 0, types: [] }; }
    }

    /** Врезать готовые записи в выход. Возвращает ТОТ ЖЕ blob, если нечего. */
    async function write(outBlob, items) {
        if (!outBlob || !items || !items.length) return outBlob;
        var udta = buildUdta(items);
        if (!udta) return outBlob;
        var m = await moovOf(outBlob);
        if (!m) return outBlob;
        if (udtaIn(m.bytes, moovIn(m.bytes))) return outBlob;     /* у выхода уже свои */
        var patched = injectMoov(m.bytes, m.box.start, udta);
        var parts = [];
        if (m.box.start > 0) parts.push(outBlob.slice(0, m.box.start));
        parts.push(patched);
        if (m.box.start + m.box.size < outBlob.size) parts.push(outBlob.slice(m.box.start + m.box.size));
        return new Blob(parts, { type: outBlob.type || 'video/mp4' });
    }

    /**
     * Передняя дверь: перенести описательные теги src → out.
     * Возвращает ТОТ ЖЕ out, если переносить нечего или что-то не сошлось.
     */
    async function carry(src, outBlob, opts) {
        opts = opts || {};
        try {
            if (!src || !outBlob || !outBlob.size) return outBlob;

            var sm = await moovOf(src);
            if (!sm) return outBlob;
            var items = collectItems(sm.bytes);
            if (!items.length) { note(opts, 'none', 0); return outBlob; }

            var om = await moovOf(outBlob);
            if (!om) { note(opts, 'no_moov', items.length); return outBlob; }
            if (udtaIn(om.bytes, moovIn(om.bytes))) { note(opts, 'kept', items.length); return outBlob; }

            var res = await write(outBlob, items);
            note(opts, res === outBlob ? 'skip' : 'ok', items.length);
            return res;
        } catch (e) {
            note(opts, 'fail', 0, e);
            return outBlob;
        }
    }

    /**
     * Источник, который сейчас лежит на странице, — или null.
     *
     * Зачем такая дверь вообще существует. Общий видеоэкспортёр
     * (canvas-video-export) собирает контейнер из КАДРОВ и исходного файла на
     * руках не держит, а потребителей у него три десятка. Обязать каждого
     * передавать источник — значит сделать правило списком, который следующий
     * тул не пополнит: именно так теги и терялись. Поэтому спрашиваем того,
     * кто ответ знает по должности, — общую зону загрузки.
     *
     * Осторожность здесь важнее полноты, поэтому отвечаем только на
     * ОДНОЗНАЧНЫЙ вопрос:
     *   · ровно один файл mp4-семейства на всей странице → он и есть источник;
     *   · несколько файлов (склейка, слайд-шоу) → null: чей это выход, знает
     *     тул, а не мы;
     *   · не mp4 (фото, gif, wav) → null: переносить нечего по построению.
     * Плюс сам `carry` отказывается трогать выход, у которого теги уже есть.
     */
    function pageSource() {
        try {
            if (typeof document === 'undefined') return null;
            if (typeof window !== 'undefined' && window.__mp4TagsOff) return null;
            var zones = document.querySelectorAll('.tool-uploader'), all = [];
            for (var i = 0; i < zones.length; i++) {
                var d = zones[i].__deliveredFiles;
                if (d && d.length) all = all.concat(Array.prototype.slice.call(d));
            }
            if (all.length !== 1) return null;
            return looksMp4(all[0]) ? all[0] : null;
        } catch (_) { return null; }
    }

    function note(opts, outcome, n, e) {
        try {
            if (typeof window === 'undefined' || !window.__ae) return;
            window.__ae('feature_use', {
                f: 'mp4_tags_carry', outcome: outcome, n: n,
                slug: opts.slug || '', err: e ? String(e.message || e).slice(0, 80) : ''
            });
        } catch (_) { }
    }

    return {
        carry: carry, read: read, write: write, pageSource: pageSource,
        /* открыто ради тестов и редких вызывающих со своими байтами */
        _collectItems: collectItems, _buildUdta: buildUdta, _injectMoov: injectMoov,
        _buildItem: buildItem, _children: children, MAX_BYTES: MAX_BYTES
    };
});
